import { useState, useEffect } from "react";
import { Navbar } from "@/components/Navbar";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Settings,
  FolderSearch,
  Cloud,
  HardDrive,
  CheckCircle,
  XCircle,
  RefreshCw,
  Eye,
  Film,
  Save
} from "lucide-react";

export const AdminSettings = () => {
  const [settings, setSettings] = useState({
    source_type: "local",
    local_path: "/app/media/videos",
    cloudinary_folder: "filmfinder/media/videos",
    cloudinary_configured: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [cloudinaryTest, setCloudinaryTest] = useState(null);
  const [scanPreview, setScanPreview] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings/media`);
      setSettings(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des paramètres");
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings/media`, {
        source_type: settings.source_type,
        local_path: settings.local_path,
        cloudinary_folder: settings.cloudinary_folder
      });
      toast.success("Paramètres sauvegardés");
      setScanPreview(null);
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const testCloudinary = async () => {
    setTesting(true);
    try {
      const response = await axios.get(`${API}/admin/settings/media/test-cloudinary`);
      setCloudinaryTest(response.data);
      if (response.data.success) {
        toast.success("Connexion Cloudinary réussie");
      } else {
        toast.error(response.data.error || "Échec de la connexion");
      }
    } catch (error) {
      toast.error("Erreur lors du test");
      setCloudinaryTest({ success: false, error: "Connection failed" });
    } finally {
      setTesting(false);
    }
  };

  const previewScan = async () => {
    setScanning(true);
    try {
      const response = await axios.get(`${API}/admin/settings/media/scan-preview`);
      setScanPreview(response.data);
    } catch (error) {
      toast.error("Erreur lors de l'aperçu");
    } finally {
      setScanning(false);
    }
  };

  const triggerScan = async () => {
    try {
      await axios.post(`${API}/admin/scan-media`);
      toast.success("Scan lancé en arrière-plan");
    } catch (error) {
      toast.error("Erreur lors du lancement du scan");
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "0 B";
    const gb = bytes / (1024 * 1024 * 1024);
    if (gb >= 1) return `${gb.toFixed(2)} Go`;
    const mb = bytes / (1024 * 1024);
    if (mb >= 1) return `${mb.toFixed(2)} Mo`;
    const kb = bytes / 1024;
    return `${kb.toFixed(2)} Ko`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <Navbar />
        <div className="pt-24 flex items-center justify-center">
          <div className="w-12 h-12 border-4 border-violet-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="admin-settings">
      <Navbar />

      <div className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="font-['Outfit'] text-3xl font-bold text-white flex items-center gap-3">
              <Settings className="w-8 h-8 text-violet-500" />
              Paramètres des médias
            </h1>
            <p className="text-gray-400 mt-1">
              Configurez l'emplacement de vos fichiers vidéo
            </p>
          </div>

          {/* Source Selection */}
          <Card className="bg-[#171717] border-white/10 mb-6">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <FolderSearch className="w-5 h-5 text-violet-500" />
                Source des fichiers
              </CardTitle>
              <CardDescription>
                Choisissez où sont stockés vos fichiers vidéo
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label className="text-gray-300">Type de source</Label>
                <Select
                  value={settings.source_type}
                  onValueChange={(value) => setSettings({ ...settings, source_type: value })}
                >
                  <SelectTrigger className="bg-black/30 border-white/10" data-testid="source-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#171717] border-white/10">
                    <SelectItem value="local" className="hover:bg-white/5">
                      <div className="flex items-center gap-2">
                        <HardDrive className="w-4 h-4" />
                        Disque local
                      </div>
                    </SelectItem>
                    <SelectItem value="cloudinary" className="hover:bg-white/5">
                      <div className="flex items-center gap-2">
                        <Cloud className="w-4 h-4" />
                        Cloudinary
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Local Path */}
              {settings.source_type === "local" && (
                <div className="space-y-2 animate-fade-in">
                  <Label className="text-gray-300">Chemin du dossier local</Label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <HardDrive className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                      <Input
                        value={settings.local_path}
                        onChange={(e) => setSettings({ ...settings, local_path: e.target.value })}
                        className="pl-10 bg-black/30 border-white/10 focus:border-violet-500"
                        placeholder="/app/media/videos"
                        data-testid="local-path-input"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">
                    Chemin absolu vers le dossier contenant vos fichiers vidéo
                  </p>
                </div>
              )}

              {/* Cloudinary Settings */}
              {settings.source_type === "cloudinary" && (
                <div className="space-y-4 animate-fade-in">
                  <div className="space-y-2">
                    <Label className="text-gray-300">Dossier Cloudinary</Label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Cloud className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                        <Input
                          value={settings.cloudinary_folder}
                          onChange={(e) => setSettings({ ...settings, cloudinary_folder: e.target.value })}
                          className="pl-10 bg-black/30 border-white/10 focus:border-violet-500"
                          placeholder="filmfinder/media/videos"
                          data-testid="cloudinary-folder-input"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-gray-500">
                      Chemin du dossier dans Cloudinary (ex: filmfinder/media/videos)
                    </p>
                  </div>

                  {/* Cloudinary Status */}
                  <div className="flex items-center justify-between p-4 rounded-lg bg-black/30 border border-white/10">
                    <div className="flex items-center gap-3">
                      {settings.cloudinary_configured ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-500" />
                      )}
                      <div>
                        <p className="text-white font-medium">
                          {settings.cloudinary_configured ? "Cloudinary configuré" : "Cloudinary non configuré"}
                        </p>
                        <p className="text-xs text-gray-500">
                          {settings.cloudinary_configured
                            ? "Les credentials sont définis dans le backend"
                            : "Ajoutez les credentials dans le fichier .env"}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={testCloudinary}
                      disabled={testing || !settings.cloudinary_configured}
                      className="border-white/10 hover:bg-white/5"
                      data-testid="test-cloudinary-btn"
                    >
                      {testing ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        "Tester"
                      )}
                    </Button>
                  </div>

                  {/* Cloudinary Test Results */}
                  {cloudinaryTest && (
                    <div className={`p-4 rounded-lg border ${
                      cloudinaryTest.success
                        ? "bg-green-500/10 border-green-500/30"
                        : "bg-red-500/10 border-red-500/30"
                    }`}>
                      {cloudinaryTest.success ? (
                        <div className="space-y-2">
                          <p className="text-green-400 font-medium flex items-center gap-2">
                            <CheckCircle className="w-4 h-4" />
                            Connexion réussie
                          </p>
                          <p className="text-sm text-gray-400">
                            Cloud: <span className="text-white">{cloudinaryTest.cloud_name}</span>
                          </p>
                          <p className="text-sm text-gray-400">
                            Vidéos trouvées: <span className="text-white">{cloudinaryTest.video_count}</span>
                          </p>
                          {cloudinaryTest.root_folders?.length > 0 && (
                            <p className="text-sm text-gray-400">
                              Dossiers racine: <span className="text-white">{cloudinaryTest.root_folders.join(", ")}</span>
                            </p>
                          )}
                        </div>
                      ) : (
                        <p className="text-red-400">
                          Erreur: {cloudinaryTest.error}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Save Button */}
              <div className="flex gap-3 pt-4 border-t border-white/10">
                <Button
                  onClick={saveSettings}
                  disabled={saving}
                  className="bg-violet-600 hover:bg-violet-500"
                  data-testid="save-settings-btn"
                >
                  {saving ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Sauvegarder
                </Button>
                <Button
                  variant="outline"
                  onClick={previewScan}
                  disabled={scanning}
                  className="border-white/10 hover:bg-white/5"
                  data-testid="preview-scan-btn"
                >
                  {scanning ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Eye className="w-4 h-4 mr-2" />
                  )}
                  Aperçu des fichiers
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Scan Preview */}
          {scanPreview && (
            <Card className="bg-[#171717] border-white/10 mb-6 animate-fade-in">
              <CardHeader>
                <CardTitle className="text-white flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Film className="w-5 h-5 text-violet-500" />
                    Fichiers détectés
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-violet-500/20 text-violet-300">
                      {scanPreview.total} fichiers
                    </Badge>
                    <Badge className="bg-green-500/20 text-green-300">
                      {scanPreview.new_files} nouveaux
                    </Badge>
                  </div>
                </CardTitle>
                <CardDescription>
                  Source: {scanPreview.source_type === "cloudinary" ? "Cloudinary" : "Disque local"}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {scanPreview.files?.length > 0 ? (
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {scanPreview.files.map((file, idx) => (
                      <div
                        key={idx}
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          file.already_imported
                            ? "bg-black/20 border-white/5"
                            : "bg-green-500/5 border-green-500/20"
                        }`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <Film className={`w-5 h-5 flex-shrink-0 ${
                            file.already_imported ? "text-gray-500" : "text-green-400"
                          }`} />
                          <div className="min-w-0">
                            <p className={`font-medium truncate ${
                              file.already_imported ? "text-gray-400" : "text-white"
                            }`}>
                              {file.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {formatFileSize(file.size)}
                            </p>
                          </div>
                        </div>
                        {file.already_imported && (
                          <Badge className="bg-gray-500/20 text-gray-400 flex-shrink-0">
                            Déjà importé
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-400">
                    <Film className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Aucun fichier vidéo trouvé</p>
                  </div>
                )}

                {scanPreview.new_files > 0 && (
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <Button
                      onClick={triggerScan}
                      className="w-full bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-500 hover:to-pink-500"
                      data-testid="trigger-scan-btn"
                    >
                      <FolderSearch className="w-4 h-4 mr-2" />
                      Importer {scanPreview.new_files} nouveau{scanPreview.new_files > 1 ? "x" : ""} fichier{scanPreview.new_files > 1 ? "s" : ""}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};
